import type { ColorProps } from '../../../utilities/shared/Color';
import type { PortIndicator, PortIndicatorPlacement } from '../../Port/utils';
import { IconTooltip } from '../../../components/Tooltip/Tooltip';
import { styled } from '../../../stitches.config';
import { chassisVars } from '../../Chassis/atoms/utils';

const SlotIndicatorsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  widthAll: `$${chassisVars.slot.width}`,
  heightAll: `$${chassisVars.slot.indicator}`,
  padding: `0 $${chassisVars.slot.indicatorInset}`,
});
SlotIndicatorsContainer.displayName = 'SlotIndicatorsContainer';

/**
 * One end of the rail. Both are always rendered — an empty cell is what holds the other on its
 * column.
 *
 * `start` takes its natural width so its icons pack leftward from the rail's inset, and `end` is
 * pinned to one icon so a slot with nothing on the left doesn't let the uplink drift inward. Nothing
 * between them: the rail is cut to exactly three icons and a pixel either side, so a full one draws
 * them shoulder to shoulder and anything lighter is spread by `space-between` instead. A gap here
 * would come out of the third icon.
 *
 * Two icons on the left is the most that clears the right, and `getPortIndicators` won't hand over a
 * third: neither cell can shrink below its icons, so a third would run the row long and carry the
 * uplink out of its column. Build the array any other way and that is on the caller.
 */
const SlotIndicatorsCell = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',

  variants: {
    placement: {
      start: {
        justifyContent: 'flex-start',
      },
      end: {
        justifyContent: 'flex-end',
        widthAll: `$${chassisVars.slot.indicatorIcon}`,
      },
    },
  },
});
SlotIndicatorsCell.displayName = 'SlotIndicatorsCell';

const slotIndicatorPlacements = ['start', 'end'] as const satisfies PortIndicatorPlacement[];

/**
 * Whether any of these indicators draws on the rail rather than in the caption.
 *
 * A slot builds its rail from this rather than from having been handed an array at all: a port whose
 * only property is PoE puts nothing out here, and reserving the rail's height for it would leave a
 * band of empty pixels between the glyph and its number on every faceplate that has no uplink to
 * show — an access point's, in practice. Rows stay level regardless, because a slot's glyph and
 * caption are pinned to its edges and any unused height collects behind the rail. See `SlotTarget`.
 */
export const hasRailIndicators = (indicators: PortIndicator[] | undefined) =>
  indicators?.some((indicator) =>
    slotIndicatorPlacements.some((placement) => placement === indicator.placement),
  ) ?? false;

/** A shade below the rail's grey. Paired with `variant="inherit"`, or the variant colours it too. */
const mutedIndicatorColor = {
  light: 'disabledNeutralLight',
  dark: 'disabledNeutralDark',
} satisfies ColorProps;

/**
 * One indicator, at the size every position draws them — the rail and the caption have to agree, or
 * a port's bolt wouldn't match the arrow it sits under.
 *
 * Colour is the indicator's to choose and `neutral` is what almost all of them choose, which is the
 * point: a grey rail is a background the one icon reporting something happening right now can stand
 * out from. See `getPortIndicators`.
 */
export function SlotIndicatorIcon({ indicator }: { indicator: PortIndicator }) {
  return (
    <IconTooltip
      icon={indicator.icon}
      size={chassisVars.slot.indicatorIcon}
      variant={indicator.muted ? 'inherit' : indicator.variant ?? 'neutral'}
      color={indicator.muted ? mutedIndicatorColor : undefined}
      contents={indicator.tooltip}
    />
  );
}

export type SlotIndicatorsProps = {
  indicators: PortIndicator[];
};

/**
 * The rail of property icons along a slot's outer edge, laid out by placement rather than by order.
 *
 * Uplink holds the right of every slot, so it reads down a whole row without anyone having to look
 * at an icon; everything else packs against the left. Indicators placed in the `caption` are drawn
 * by `SlotCaption` instead and ignored here, so a caller can hand the rail a port's whole set. See
 * `getPortIndicators`.
 */
export function SlotIndicators({ indicators }: SlotIndicatorsProps) {
  return (
    <SlotIndicatorsContainer>
      {slotIndicatorPlacements.map((placement) => (
        <SlotIndicatorsCell key={placement} placement={placement}>
          {indicators
            .filter((indicator) => indicator.placement === placement)
            .map((indicator) => (
              <SlotIndicatorIcon key={indicator.id} indicator={indicator} />
            ))}
        </SlotIndicatorsCell>
      ))}
    </SlotIndicatorsContainer>
  );
}
